import { computed, onBeforeUnmount, ref } from "vue";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";
import { walletSummaryService } from "@/services/walletSummaryService";
import {
  ClapTransferError,
  MAX_CLAPS_PER_BATCH,
  clapBatchAtomic,
  createClapTransferIntent,
  deriveClapPlacementRef,
  getClapAvailability,
  getClapTransferErrorMessage,
  isClapTransferFeatureEnabled,
  perClapAtomic,
  transferClapMoney,
} from "@/utils/transferClapMoney";

export const CLAP_COMMIT_DELAY_MS = 3000;

/** Returns the increment and emoji for the next clap in a batch. */
export function getClapConfig(currentCount) {
  if (currentCount >= 100) return { increment: 25, emoji: "🚀" };
  if (currentCount >= 50) return { increment: 10, emoji: "🤩" };
  if (currentCount >= 20) return { increment: 5, emoji: "🥳" };
  if (currentCount > 10) return { increment: 2, emoji: "🎉" };
  return { increment: 1, emoji: "👏" };
}

/**
 * Signed-in actor and acting organisation (the payer's `fromOrgId`). Reads
 * only the Vuex session; never Firestore.
 */
export function useClapActorContext() {
  const store = useStore();
  const currentUserUID = computed(
    () =>
      store.getters.userCredentials?.data?.uid ||
      store.getters.user?.data?.uid ||
      null,
  );
  const selectedOrgUID = computed(
    () => store.getters.selectedOrgUID || store.getters.currentOrgUID || null,
  );
  const currentWallet = computed(() => store.getters.currentUserWallet || null);
  return { currentUserUID, selectedOrgUID, currentWallet };
}

const samePlacement = (left, right) =>
  !!left &&
  !!right &&
  left.itemId === right.itemId &&
  left.orgId === right.orgId;

/**
 * One clap control's batching session. Claps accumulate locally for
 * CLAP_COMMIT_DELAY_MS and are then settled as one server quote → settle
 * transfer from the acting organisation's Wallet to the item's owner.
 *
 * @param {object} options
 * @param {() => ({itemId: string, orgId: string} | null)} options.placement
 *   the item placement from resolveClapPlacement(item).
 * @param {() => (string | null)} [options.receiverUid] legacy owner UID, used
 *   only to hide self-claps on legacy cards; never sent to the server.
 */
export function useClapSession({ placement, receiverUid = () => null } = {}) {
  const { currentUserUID, selectedOrgUID } = useClapActorContext();
  const featureEnabled = isClapTransferFeatureEnabled();
  const clapCount = ref(0);
  const isProcessing = ref(false);
  let batch = null;
  let batchPreparation = null;
  let commitTimer = null;

  const currentPlacement = computed(() => placement?.() || null);
  const isOwnContent = computed(() => {
    const owner = receiverUid?.();
    return !!owner && owner === currentUserUID.value;
  });
  const availability = computed(() =>
    getClapAvailability({
      featureEnabled,
      actorUid: currentUserUID.value,
      fromOrgId: selectedOrgUID.value,
      placement: currentPlacement.value,
    }),
  );

  const isBatchCurrent = (candidate) =>
    !!candidate &&
    candidate.actorUid === currentUserUID.value &&
    candidate.fromOrgId === selectedOrgUID.value &&
    samePlacement(candidate.placement, currentPlacement.value);

  const resetBatch = () => {
    clearTimeout(commitTimer);
    commitTimer = null;
    clapCount.value = 0;
    batch = null;
  };

  const prepareBatch = async () => {
    const context = {
      actorUid: currentUserUID.value,
      fromOrgId: selectedOrgUID.value,
      placement: currentPlacement.value,
    };
    // Own Wallet only, through the server summary; sizes a clap locally.
    // The server re-checks the balance and resolves the item owner.
    const [summary, placementRef] = await Promise.all([
      walletSummaryService.getOwnWalletSummary(context.fromOrgId),
      deriveClapPlacementRef(context.placement),
    ]);
    if (summary.walletStatus !== "ready" || !summary.balances) {
      throw new ClapTransferError("clap/wallet-unavailable");
    }
    const availableAtomic = summary.balances.currentAvailableAtomic;
    const amount = perClapAtomic(availableAtomic);
    if (amount === "0") throw new ClapTransferError("clap/insufficient-funds");
    return Object.freeze({
      ...context,
      placementRef,
      perClapAtomic: amount,
      availableAtomic,
    });
  };

  const notifyUnavailable = () => {
    const message = isOwnContent.value
      ? "You cannot clap your own content."
      : availability.value.message;
    if (message) toast.info(message, { autoClose: 3000 });
  };

  const commit = async () => {
    commitTimer = null;
    if (isProcessing.value) {
      // Settle batches strictly one after another.
      commitTimer = setTimeout(commit, 500);
      return;
    }
    const count = clapCount.value;
    const frozen = batch;
    clapCount.value = 0;
    batch = null;
    if (!count || !frozen) return;
    if (!isBatchCurrent(frozen)) {
      toast.error("Your acting organisation changed. Please clap again.");
      return;
    }
    isProcessing.value = true;
    try {
      const intent = createClapTransferIntent({
        fromOrgId: frozen.fromOrgId,
        placementRef: frozen.placementRef,
        amountAtomic: clapBatchAtomic(frozen.perClapAtomic, count),
      });
      await transferClapMoney(intent, {
        featureEnabled,
        expectedPayeeOrgId: frozen.placement.orgId,
      });
      walletSummaryService.clearCache?.();
      toast.success(`Clapped ${count} ${count === 1 ? "time" : "times"}.`, {
        autoClose: 3000,
      });
    } catch (error) {
      toast.error(getClapTransferErrorMessage(error), { autoClose: 4000 });
    } finally {
      isProcessing.value = false;
    }
  };

  const scheduleCommit = () => {
    clearTimeout(commitTimer);
    commitTimer = setTimeout(commit, CLAP_COMMIT_DELAY_MS);
  };

  /**
   * Registers one user clap. Resolves with `{ increment, emoji }` when claps
   * were added to the batch, or null when the clap was refused (with a toast
   * explaining why).
   */
  const registerClap = async () => {
    if (isOwnContent.value || !availability.value.available) {
      notifyUnavailable();
      return null;
    }
    if (!batch) {
      try {
        batchPreparation ||= prepareBatch();
        batch = await batchPreparation;
      } catch (error) {
        toast.warning(getClapTransferErrorMessage(error), { autoClose: 3000 });
        return null;
      } finally {
        batchPreparation = null;
      }
    }
    if (!isBatchCurrent(batch)) {
      resetBatch();
      toast.error("Your acting organisation changed. Please clap again.");
      return null;
    }
    const remaining = MAX_CLAPS_PER_BATCH - clapCount.value;
    if (remaining <= 0) {
      toast.info("Maximum claps reached. Please wait 3 seconds...", {
        autoClose: 3000,
      });
      return null;
    }
    const config = getClapConfig(clapCount.value + 1);
    const increment = Math.min(config.increment, remaining);
    const nextTotal =
      BigInt(batch.perClapAtomic) * BigInt(clapCount.value + increment);
    if (nextTotal > BigInt(batch.availableAtomic)) {
      toast.warning(
        getClapTransferErrorMessage(
          new ClapTransferError("clap/insufficient-funds"),
        ),
        { autoClose: 3000 },
      );
      return null;
    }
    clapCount.value += increment;
    scheduleCommit();
    return { increment, emoji: config.emoji };
  };

  onBeforeUnmount(() => {
    // Claps the user already made are still sent when the card unmounts.
    if (commitTimer && clapCount.value > 0) {
      clearTimeout(commitTimer);
      commit();
    } else {
      clearTimeout(commitTimer);
    }
  });

  return {
    currentUserUID,
    selectedOrgUID,
    clapCount,
    isProcessing,
    isOwnContent,
    availability,
    registerClap,
    notifyUnavailable,
    flush: commit,
  };
}
